import { Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import {
  textOperators,
  equalityOperators,
  multiselectOperators,
  dateOperators
} from '../../../utils/constants/operators'
import { buildStatusColumn } from '../../fields/StatusField'
import { activityTypes } from '../../../utils/constants/misc'

export const getOrderColumns = ({ statusList = [], customers = [] }) => {
  const getCustomersFilter = () =>
    customers.map((customer) => ({
      name: customer.id?.toString(),
      label: customer.customer_name
    }))

  return [
    {
      name: 'order_number',
      label: 'N° Commande',
      isFilter: true,
      operators: textOperators
    },
    {
      name: 'customer_id',
      label: 'Client',
      isFilter: true,
      valueEditorType: 'multiselect',
      values: getCustomersFilter(),
      mapping: (id) => {
        const result = customers.find((item) => item.id === id)
        if (result)
          return (
            <Typography state={{ id: id, history: '/orders' }} component={Link} to={`/customers/view`}>
              {result.customer_name}
            </Typography>
          )
        else return id
      },
      operators: [...equalityOperators, ...multiselectOperators]
    },
    buildStatusColumn('orders', statusList),
    {
      name: 'summary',
      label: 'Titre',
      isFilter: false
    },
    {
      name: 'order_date',
      label: 'Date Commande',
      isFilter: true,
      inputType: 'date',
      type: 'date',
      format: 'DD/MM/YYYY',
      operators: dateOperators
    },
    {
      name: 'periodYearMonth',
      label: 'Période',
      isFilter: true,
      operators: dateOperators
    },
    { name: 'total_amount_net', label: 'Montant total', type: 'currency', isFilter: false },
    {
      display: 'quote.quote_number',
      name: 'quote_number',
      label: 'Devis',
      isFilter: false,
      operators: textOperators,
      mapping: (id, rowid) => {
        return (
          <Typography state={{ id: rowid.quote_id, history: '/orders' }} component={Link} to={`/quotes/view`}>
            {id}
          </Typography>
        )
      }
    },
    {
      name: 'createdDateTime',
      label: 'Date Création',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'createdBy',
      label: 'Créé Par',
      isFilter: true,
      operators: textOperators
    },
    {
      name: 'lastModDateTime',
      label: 'Date Modification',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'lastModBy',
      label: 'Modifié Par',
      isFilter: true,
      operators: textOperators
    }
  ]
}

export const getOrderFields = (user, location, setOrderLines) => [
  [
    {
      name: 'customer_id',
      label: 'Client',
      required: true,
      type: 'select',
      defaultValue: () => location?.state?.context?.customer_id ?? null,
      options: {
        type: 'api',
        object: 'customers',
        value: 'customer_name',
        returnField: 'id',
        returnFields: ['id'],
        filter: () => ({ owner_id: user.company_id })
      },
      sizeDivided: 2,
      fullWidth: true,
      validation: [
        {
          rule: 'req',
          error: 'Client obligatoire'
        }
      ]
    }
  ],
  [
    {
      name: 'order_number',
      label: 'N° Commande',
      readOnly: true,
      type: 'text',
      fullWidth: true,
      validationWhen: () => location.pathname === '/orders/edit',
      validation: [
        {
          rule: 'req',
          error: 'N° Commande obligatoire'
        }
      ]
    },
    {
      name: 'order_date',
      label: 'Date',
      required: true,
      type: 'date',
      fullWidth: true,
      validation: [
        {
          rule: 'req',
          error: 'Date obligatoire'
        }
      ]
    }
  ],
  [
    {
      name: 'total_amount_net',
      label: 'Montant Total',
      type: 'currency',
      fullWidth: true,
      readOnly: true,
      sizeDivided: 4
    }
  ],
  [
    {
      name: 'summary',
      label: 'Titre',
      type: 'text',
      fullWidth: true,
      sizeDivided: 2
    }
  ],
  [
    {
      name: 'lines',
      label: 'Lignes',
      type: 'mytable',
      tableOptions: {
        paperStyle: { width: '100%', margin: '0 auto' },
        setDataParent: setOrderLines,
        defaultSorted: 'id',
        columns: [
          {
            name: 'id',
            display: false,
            enableEditing: false
          },
          {
            name: 'type',
            label: 'Type',
            type: 'select',
            enableEditing: true,
            mapping: (val) => {
              return activityTypes.find((i) => i.id === val)?.label ?? val
            },
            options: {
              type: 'data',
              data: activityTypes,
              returnField: 'id',
              returnFields: ['id'],
              value: 'label'
            },
            validation: [
              {
                rule: 'req',
                error: 'Type obligatoire'
              }
            ]
          },
          {
            name: 'description',
            label: 'Description',
            type: 'freeselect',
            onChangeF: (data, priceBook, onRowDataChange) => {
              const dataN = { ...data }
              if (dataN.priceBook_id !== undefined) {
                const p = priceBook.find((p) => p.id === data.priceBook_id)
                if (p) {
                  dataN.unit_price = p.unit_price
                  dataN.quantity = p.quantity
                  dataN.net_amount = p.unit_price * p.quantity
                  dataN.type = p.type
                  dataN.priceBook_id = p.id
                }
              }
              onRowDataChange(dataN)
            },
            optionsAPI: {
              api: (props) => {
                if (props.type === 1) return 'products'
                else return 'priceBooks'
              },
              filter: () => ({ company_id: user.company_id }),
              trigger: 'type'
            },
            dataId: (props) => {
              if (props.type === 1) return 'products'
              else return 'priceBooks'
            },
            enableEditing: true,
            size: 80,
            validation: [
              {
                rule: 'req',
                error: 'Description obligatoire'
              }
            ]
          },
          {
            name: 'quantity',
            label: 'Quantité',
            enableEditing: true,
            type: 'float',
            min: 0,
            validation: [
              {
                rule: 'req',
                error: 'Quantité obligatoire'
              }
            ]
          },
          {
            name: 'unit_price',
            label: 'Prix unitaire',
            type: 'currency',
            enableEditing: true
          },
          {
            name: 'net_amount',
            label: 'Montant',
            type: 'currency',
            enableEditing: false,
            defaultValueFunction: (props) => {
              const val = parseFloat(props.quantity) * parseFloat(props.unit_price)
              return isNaN(val) ? undefined : val
            }
          }
        ],
        actions: {
          add: true,
          edit: true,
          editDisabled: (order) => order.status >= 70,
          delete: {
            message: 'Voulez-vous vraiment supprimer la ligne'
          }
        },
        title: 'Lignes',
        options: {
          pageSize: 10,
          search: false,
          paging: false,
          columnsButton: true,
          actionsColumnIndex: -1
        }
      },
      validation: [
        {
          rule: 'req',
          error: 'Saisir au moins 1 ligne'
        }
      ]
    }
  ]
]
